import React, { useState } from 'react';
import firebase from '../firebaseConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faRightFromBracket, faTrash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import brandIcon from '../icon.png';
import { useEscapeKey } from '../hooks/useEscapeKey';

const CONFIRM_PHRASE = 'delete account';

const SettingsPopup = ({ onClose }) => {
    const [signingOut, setSigningOut] = useState(false);
    const [mode, setMode] = useState('main'); // 'main' | 'confirmDelete'
    const [confirmText, setConfirmText] = useState('');
    const [password, setPassword] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');

    useEscapeKey(() => { if (!deleting) onClose(); });

    const user = firebase.auth().currentUser;
    const email = user?.email || '';
    const displayName = user?.displayName || '';
    const providerId = user?.providerData?.[0]?.providerId || 'password';
    const isPasswordProvider = providerId === 'password';

    const signOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        try {
            await firebase.auth().signOut();
        } finally {
            setSigningOut(false);
            onClose();
        }
    };

    const reauthenticate = async () => {
        const current = firebase.auth().currentUser;
        if (providerId === 'google.com') {
            const provider = new firebase.auth.GoogleAuthProvider();
            await current.reauthenticateWithPopup(provider);
        } else if (providerId === 'apple.com') {
            const provider = new firebase.auth.OAuthProvider('apple.com');
            await current.reauthenticateWithPopup(provider);
        } else if (providerId === 'password') {
            const cred = firebase.auth.EmailAuthProvider.credential(email, password);
            await current.reauthenticateWithCredential(cred);
        } else {
            throw new Error('This sign-in method does not support account deletion.');
        }
    };

    const deleteAccountErrorMessage = (err) => {
        const code = err?.code || '';
        if (
            code === 'auth/popup-closed-by-user' ||
            code === 'auth/cancelled-popup-request' ||
            code === 'auth/user-cancelled'
        ) return 'Account deletion canceled.';
        if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
            return 'Incorrect password.';
        }
        if (code === 'auth/user-mismatch') {
            return 'That account does not match the one you are signed in as.';
        }
        if (code === 'functions/failed-precondition') {
            return 'Please reauthenticate and try again.';
        }
        if (code === 'functions/resource-exhausted') {
            return 'Too many attempts. Try again later.';
        }
        return err?.message || 'Could not delete account. Try again later.';
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        if (deleting) return;
        if (confirmText.trim().toLowerCase() !== CONFIRM_PHRASE) {
            setError(`Type "${CONFIRM_PHRASE}" to confirm.`);
            return;
        }
        if (isPasswordProvider && !password) {
            setError('Enter your password to confirm.');
            return;
        }
        setDeleting(true);
        setError('');
        try {
            await reauthenticate();
            // The CF gates on a fresh `auth_time` claim — force a token
            // refresh so the just-completed reauth is reflected.
            await firebase.auth().currentUser.getIdToken(true);
            const fn = firebase.functions().httpsCallable('deleteAccount');
            await fn({ confirm: true });
            await firebase.auth().signOut();
        } catch (err) {
            setDeleting(false);
            setError(deleteAccountErrorMessage(err));
        }
    };

    const closeConfirm = () => {
        setMode('main');
        setConfirmText('');
        setPassword('');
        setError('');
    };

    const confirmReady =
        confirmText.trim().toLowerCase() === CONFIRM_PHRASE &&
        (!isPasswordProvider || password.length > 0);

    return (
        <div className="modal-backdrop" onClick={() => { if (!deleting) onClose(); }}>
            <div
                className="modal-card settings-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Settings"
            >
                {mode === 'main' ? (
                    <>
                        <div className="modal-head">
                            <div className="modal-head-text">
                                <div className="modal-eyebrow">SETTINGS</div>
                                <h2 className="modal-title">Account</h2>
                            </div>
                            <button className="modal-close" onClick={onClose} aria-label="Close">
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>

                        <div className="settings-section">
                            <div className="settings-row">
                                <div className="settings-label">Signed in as</div>
                                <div className="settings-value">{displayName || email || 'Account'}</div>
                            </div>
                            {email && displayName && (
                                <div className="settings-row">
                                    <div className="settings-label">Email</div>
                                    <div className="settings-value">{email}</div>
                                </div>
                            )}
                        </div>

                        <div className="settings-actions">
                            <button
                                className="settings-action"
                                onClick={signOut}
                                disabled={signingOut}
                            >
                                <FontAwesomeIcon icon={faRightFromBracket} />
                                <span>{signingOut ? 'Signing out…' : 'Sign out'}</span>
                            </button>
                            <button
                                className="settings-action danger"
                                onClick={() => { setError(''); setMode('confirmDelete'); }}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                <span>Delete account</span>
                            </button>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleDelete}>
                        <div className="modal-head">
                            <div className="modal-head-text">
                                <div className="modal-eyebrow">DELETE ACCOUNT</div>
                                <h2 className="modal-title">This can't be undone</h2>
                            </div>
                        </div>

                        <div className="settings-danger-note">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            <span>
                                Deleting your account also deletes any artists you're the
                                only member of. Files are retained for 14 days, then
                                permanently erased.
                            </span>
                        </div>

                        <label className="settings-confirm-label">
                            Type <strong>delete account</strong> to confirm
                        </label>
                        <input
                            className="new-project-input"
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="delete account"
                            autoFocus
                            disabled={deleting}
                        />

                        {isPasswordProvider && (
                            <input
                                className="new-project-input settings-confirm-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                autoComplete="current-password"
                                disabled={deleting}
                            />
                        )}

                        {error && <div className="new-project-error">{error}</div>}

                        <div className="new-project-actions">
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={closeConfirm}
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-primary btn-danger"
                                disabled={deleting || !confirmReady}
                            >
                                {deleting ? 'Deleting…' : 'Delete account'}
                            </button>
                        </div>
                    </form>
                )}

                <div className="brand-footer" aria-hidden="true">
                    <img src={brandIcon} alt="" className="brand-footer-logo" />
                    <span className="brand-footer-name">demodeck</span>
                    <span className="brand-footer-meta">web</span>
                </div>
            </div>
        </div>
    );
};

export default SettingsPopup;
